import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import dayjs, { Dayjs } from 'dayjs'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { RoomRateTab } from '@/components/pages/setting/room-rate/tab'
import { settingHeaderContainerStyle } from '@/components/pages/setting/guest-room/room/layout'
import { Button } from '@/components/atoms/button'
import { Popup } from '@/components/atoms/popup'
import { MonthCascaderField } from '@/components/molecules/month-cascader-field'
import { BulkSettingModal } from '@/components/pages/setting/room-rate/calendar/_calendar/bulk-setting-modal'
import type { BulkSettingFormValues } from '@/components/pages/setting/room-rate/calendar/_calendar/bulk-setting-modal'
import { MemoPopover } from '@/components/pages/setting/room-rate/calendar/_calendar/memo-popover'
import { bulkUpdateRateCalendars, fetchRateCalendars, updateRateCalendar } from '@/apis/aipass'
import { usePriceRanks } from '@/hooks/use-price-ranks'
import { useSettingsEditGuard } from '@/hooks/use-settings-edit-guard'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { isStaffGuardCancelledError } from '@/libs/errors'
import { LoaderContextCreator } from '@/contexts/loader'
import { getPriceRankColorHex, UNRESOLVED_RANK_COLOR } from '@/models/room-rate/price-rank'
import {
  buildMonthGrid,
  getMinEditableDateKey,
  isRateCalendarDateEditable,
  RATE_CALENDAR_WEEKDAY_KEYS,
  RATE_CALENDAR_WEEKDAY_TEXT_COLORS,
  resolveCalendarCellDisplay,
  toDateKey,
  toRateCalendarDayMap,
} from '@/models/room-rate/rate-calendar'
import type { RateCalendarDayMap } from '@/models/room-rate/rate-calendar'

const MONTH_PICKER_MIN = '2020-01-01'
const MONTH_PICKER_MAX = '2035-12-01'
const MEMO_POPOVER_HEIGHT = 140
const MEMO_POPOVER_WIDTH = 280
const RANK_POPOVER_MAX_HEIGHT = 220
const DISABLED_CELL_BACKGROUND = '#F2F2F2'
const DISABLED_PILL_OVERLAY = 'rgba(242, 242, 242, 0.5)'

/** 下にはみ出すときは上に出す。カレンダー枠の overflow で切れないよう fixed 用の座標を返す。 */
const placePopover = (rect: DOMRect, height: number, width: number, alignLeft: number) => {
  const margin = 4
  const openUp = rect.bottom + margin + height > window.innerHeight
  const top = openUp ? Math.max(8, rect.top - margin - height) : rect.bottom + margin
  const left = Math.max(8, Math.min(alignLeft, window.innerWidth - width - 8))
  return { top, left }
}

export const SettingRoomRateCalendar: React.FC = () => {
  const { t } = useTranslation()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { errorHandler } = useErrorHandler()
  const { isSettingsReadOnly, guardSettingsEdit } = useSettingsEditGuard()
  const { ranks, ranksLoaded, loadPriceRanks } = usePriceRanks()
  const [isPageLoading, setIsPageLoading] = useState(false)
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(() => dayjs().startOf('month'))
  const [dayMap, setDayMap] = useState<RateCalendarDayMap>({})
  const [rankPopover, setRankPopover] = useState<{ dateKey: string; top: number; left: number; width: number } | null>(null)
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [memoTarget, setMemoTarget] = useState<{ dateKey: string; top: number; left: number; readOnly: boolean } | null>(null)
  const calendarRequestId = useRef(0)

  const year = currentMonth.year()
  const month = currentMonth.month() + 1
  const cells = useMemo(() => buildMonthGrid(year, month), [year, month])

  const fetchCalendarMap = async (targetYear: number, targetMonth: number): Promise<RateCalendarDayMap> => {
    const prev = dayjs(`${targetYear}-${String(targetMonth).padStart(2, '0')}-01`).subtract(1, 'month')
    const next = dayjs(`${targetYear}-${String(targetMonth).padStart(2, '0')}-01`).add(1, 'month')
    const [currentEntries, prevEntries, nextEntries] = await Promise.all([
      fetchRateCalendars(targetYear, targetMonth),
      fetchRateCalendars(prev.year(), prev.month() + 1),
      fetchRateCalendars(next.year(), next.month() + 1),
    ])
    return {
      ...toRateCalendarDayMap(prevEntries || []),
      ...toRateCalendarDayMap(nextEntries || []),
      ...toRateCalendarDayMap(currentEntries || []),
    }
  }

  const reloadCalendars = async (targetYear: number, targetMonth: number) => {
    const requestId = ++calendarRequestId.current
    const map = await fetchCalendarMap(targetYear, targetMonth)
    if (requestId !== calendarRequestId.current) return
    setDayMap(map)
  }

  useEffect(() => {
    let cancelled = false
    const loadRanks = async () => {
      try {
        await loadPriceRanks()
      } catch (e) {
        if (!cancelled) {
          errorHandler(e)
        }
      }
    }
    loadRanks()
    return () => {
      cancelled = true
    }
  }, [loadPriceRanks])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const requestId = ++calendarRequestId.current
      setIsPageLoading(true)
      try {
        const map = await fetchCalendarMap(year, month)
        if (cancelled || requestId !== calendarRequestId.current) return
        setDayMap(map)
      } catch (e) {
        if (!cancelled) {
          errorHandler(e)
        }
      } finally {
        if (!cancelled) {
          setIsPageLoading(false)
        }
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [year, month])

  const goPrevMonth = () => setCurrentMonth(prev => prev.clone().subtract(1, 'month'))
  const goNextMonth = () => setCurrentMonth(prev => prev.clone().add(1, 'month'))

  const canEditDate = (dateKey: string) => !isSettingsReadOnly && isRateCalendarDateEditable(dateKey)

  const onChangeRank = async (dateKey: string, priceRankId: string) => {
    if (!canEditDate(dateKey) || !guardSettingsEdit()) {
      setRankPopover(null)
      return
    }
    try {
      setIsLoading(true)
      await updateRateCalendar({ date: dateKey, priceRankId })
      await reloadCalendars(year, month)
      setRankPopover(null)
    } catch (e) {
      if (!isStaffGuardCancelledError(e)) {
        errorHandler(e)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onChangeMemo = async (dateKey: string, memo: string) => {
    if (!canEditDate(dateKey) || !guardSettingsEdit()) {
      return
    }
    const priceRankId = dayMap[dateKey]?.priceRankId
    // 削除済み・未ロード中の ID で PUT しない
    if (!priceRankId || !ranks.some(rank => rank.id === priceRankId)) {
      return
    }
    try {
      setIsLoading(true)
      // 空判定と送信値を揃える（前後空白だけの入力はクリア扱い）
      const trimmedMemo = memo.trim()
      await updateRateCalendar({
        date: dateKey,
        priceRankId,
        memo: trimmedMemo === '' ? null : trimmedMemo,
      })
      await reloadCalendars(year, month)
    } catch (e) {
      if (!isStaffGuardCancelledError(e)) {
        errorHandler(e)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onBulkChange = async (values: BulkSettingFormValues) => {
    if (!guardSettingsEdit()) {
      return
    }
    try {
      setIsLoading(true)
      await bulkUpdateRateCalendars({
        from: values.beginDate,
        to: values.endDate,
        daysOfWeek: values.weekdays,
        priceRankId: values.priceRankId,
      })
      await reloadCalendars(year, month)
      setShowBulkModal(false)
    } catch (e) {
      if (!isStaffGuardCancelledError(e)) {
        errorHandler(e)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const minEditableDate = getMinEditableDateKey()
  const monthStart = toDateKey(year, month, 1)
  const monthEnd = toDateKey(year, month, new Date(year, month, 0).getDate())
  // 月全体が編集不可（過去）なら期間は空にする。最小日に切り上げると表示中の月と関係ない「最小日〜最小日」になるため
  const isMonthPast = monthEnd < minEditableDate
  const defaultBeginDate = isMonthPast ? '' : monthStart < minEditableDate ? minEditableDate : monthStart
  const defaultEndDate = isMonthPast ? '' : monthEnd

  return (
    <SettingsLayout loading={isPageLoading}>
      <>
        <div css={settingHeaderContainerStyle}>
          <RoomRateTab currentTab="calendar" />
        </div>
        <div css={pageBodyStyle}>
          <div css={toolbarStyle}>
            <div css={monthNavStyle}>
              <div css={navLinkStyle} className="prev" onClick={goPrevMonth}>
                <img src={require('@/static/images/arrow_right_gray_nospace.svg')} alt="" />
                <span>{t('Previous month')}</span>
              </div>
              <div css={monthPickerWrapStyle}>
                <MonthCascaderField
                  // Cascader は defaultValue でしか選択月を持たないため、前月・次月で月が変わったら作り直して選択を追従させる
                  key={currentMonth.format('YYYY-MM')}
                  value={currentMonth}
                  onChange={setCurrentMonth}
                  min={MONTH_PICKER_MIN}
                  max={MONTH_PICKER_MAX}
                  width="94px"
                />
              </div>
              <div css={navLinkStyle} className="next" onClick={goNextMonth}>
                <span>{t('Next month')}</span>
                <img src={require('@/static/images/arrow_right_gray_nospace.svg')} alt="" />
              </div>
            </div>
            <Button
              buttonType={isSettingsReadOnly || !ranksLoaded || ranks.length === 0 ? 5 : 3}
              width={96}
              height={32}
              fontSize={12}
              isDisabled={isSettingsReadOnly || !ranksLoaded || ranks.length === 0}
              onClick={() => {
                if (!ranksLoaded || ranks.length === 0 || !guardSettingsEdit()) return
                setShowBulkModal(true)
              }}
            >
              {t('Bulk change')}
            </Button>
          </div>
          <p css={hintTextStyle}>{t('rateCalendar.unsavedDayHint')}</p>

          <div css={calendarCardStyle}>
            <div css={weekdayHeaderStyle}>
              {RATE_CALENDAR_WEEKDAY_KEYS.map(day => (
                <div key={day} css={[weekdayCellStyle, day === 0 && weekdaySunStyle, day === 6 && weekdaySatStyle]}>
                  {t(`rateCalendar.weekday.${day}`)}
                </div>
              ))}
            </div>

            <div css={calendarGridStyle}>
              {cells.map(cell => {
                const dayData = dayMap[cell.dateKey]
                const display = resolveCalendarCellDisplay(cell.dateKey, dayMap, ranks)
                // ranks 未取得中は orphan と誤表示しない
                const isOrphan = ranksLoaded && display.status === 'orphan'
                const color = display.status === 'matched' ? getPriceRankColorHex(display.rank.color) : UNRESOLVED_RANK_COLOR
                const rankLabel = display.status === 'matched' ? display.rank.shortName : isOrphan ? t('rateCalendar.missingRank') : '-'
                const hasMemo = Boolean(dayData?.memo)
                const isRankOpen = rankPopover?.dateKey === cell.dateKey
                const isEditable = canEditDate(cell.dateKey)
                // 突合できたランクがある日のみメモ編集可（orphan PUT / 偽 orphan を防ぐ）
                const canEditMemo = isEditable && display.status === 'matched'
                // 過去日は表示だけ。更新 API は呼ばない
                const canViewMemo = !isEditable

                return (
                  <div key={cell.dateKey} css={[dayCellStyle, !isEditable && disabledCellStyle]}>
                    <div css={dayCellHeaderStyle}>
                      <span
                        css={[
                          dayNumberStyle,
                          !isEditable && dayNumberDisabledStyle,
                          // 前月・翌月の日も当月と同じく色を付ける。土曜の祝日は赤を優先する
                          cell.weekday === 6 && dayNumberSatStyle,
                          (cell.weekday === 0 || dayData?.isNationalHoliday) && dayNumberSunStyle,
                        ]}
                      >
                        {cell.day}
                      </span>
                      <button
                        type="button"
                        css={memoButtonStyle}
                        disabled={!canEditMemo && !canViewMemo}
                        onClick={e => {
                          e.stopPropagation()
                          if (canEditMemo) {
                            if (!guardSettingsEdit()) return
                          } else if (!canViewMemo) {
                            return
                          }
                          const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect()
                          const { top, left } = placePopover(rect, MEMO_POPOVER_HEIGHT, MEMO_POPOVER_WIDTH, rect.left - 200)
                          setMemoTarget({
                            dateKey: cell.dateKey,
                            top,
                            left,
                            readOnly: !canEditMemo,
                          })
                          setRankPopover(null)
                        }}
                      >
                        <img
                          alt=""
                          src={
                            hasMemo
                              ? require('@/static/images/room-rate/memo-filled.png')
                              : require('@/static/images/room-rate/memo-empty.png')
                          }
                          css={memoIconStyle}
                        />
                      </button>
                    </div>

                    <div css={rankSelectWrapStyle} onClick={e => e.stopPropagation()}>
                      <button
                        type="button"
                        css={[rankPillStyle, { background: color }, !isEditable && disabledRankPillStyle]}
                        disabled={!isEditable}
                        onClick={e => {
                          if (!isEditable || !guardSettingsEdit()) return
                          if (isRankOpen) {
                            setRankPopover(null)
                            return
                          }
                          const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect()
                          const itemCount = ranks.length + (isOrphan ? 1 : 0)
                          const height = Math.min(RANK_POPOVER_MAX_HEIGHT, itemCount * 36 + 8)
                          const { top, left } = placePopover(rect, height, rect.width, rect.left)
                          setRankPopover({ dateKey: cell.dateKey, top, left, width: rect.width })
                          setMemoTarget(null)
                        }}
                      >
                        <span css={rankPillTextStyle}>{rankLabel}</span>
                        {isEditable && <span css={rankChevronStyle} />}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {showBulkModal && (
          <BulkSettingModal
            ranks={ranks}
            minDate={minEditableDate}
            defaultBeginDate={defaultBeginDate}
            defaultEndDate={defaultEndDate}
            visibleMonth={monthStart}
            onClickCancel={() => setShowBulkModal(false)}
            onClickChange={onBulkChange}
          />
        )}

        {rankPopover && (
          <Popup isOpen onClose={() => setRankPopover(null)} customStyle={rankPopoverStyle(rankPopover)}>
            <ul>
              {ranksLoaded && resolveCalendarCellDisplay(rankPopover.dateKey, dayMap, ranks).status === 'orphan' && (
                <li css={[rankPopupItemStyle, rankPopupItemDisabledStyle]}>
                  <span css={rankPopupColorStyle} style={{ background: UNRESOLVED_RANK_COLOR }} />
                  {t('rateCalendar.missingRank')}
                </li>
              )}
              {ranks.map(r => (
                <li key={r.id} css={rankPopupItemStyle} onClick={() => onChangeRank(rankPopover.dateKey, r.id)}>
                  <span css={rankPopupColorStyle} style={{ background: getPriceRankColorHex(r.color) }} />
                  {r.shortName}
                </li>
              ))}
            </ul>
          </Popup>
        )}

        {memoTarget && (
          <MemoPopover
            key={memoTarget.dateKey}
            dateKey={memoTarget.dateKey}
            memo={dayMap[memoTarget.dateKey]?.memo || ''}
            position={{ top: memoTarget.top, left: memoTarget.left }}
            readOnly={memoTarget.readOnly}
            onChangeMemo={memo => onChangeMemo(memoTarget.dateKey, memo)}
            onClose={() => setMemoTarget(null)}
          />
        )}
      </>
    </SettingsLayout>
  )
}

const pageBodyStyle = css({
  // カレンダー外側の左右マージンは付けない（マス内余白は dayCell 側で Figma に合わせる）
  padding: '16px 0 32px',
  background: '#fff',
  minHeight: 'calc(100vh - 120px)',
})

const toolbarStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 24px',
})

const hintTextStyle = css({
  margin: '16px 24px',
  fontSize: 12,
  lineHeight: '18px',
  color: '#676767',
})

const monthNavStyle = css({
  display: 'flex',
  alignItems: 'center',
})

const navLinkStyle = css({
  display: 'flex',
  alignItems: 'center',
  color: '#676767',
  fontSize: 12,
  fontWeight: 'bold',
  cursor: 'pointer',
  userSelect: 'none',
  img: {
    height: 14,
  },
  '&.prev': {
    span: {
      padding: '0 16px 0 8px',
    },
    img: {
      transform: 'rotate(180deg)',
    },
  },
  '&.next': {
    span: {
      padding: '0 8px 0 16px',
    },
  },
  '&:hover': {
    opacity: 0.8,
  },
})

const monthPickerWrapStyle = css({
  input: {
    height: '32px !important',
    borderRadius: '30px !important',
    border: '1px solid #CCCCCC !important',
    background: '#fff !important',
    textAlign: 'center',
    fontSize: '12px !important',
    fontWeight: 'bold',
    color: '#272727 !important',
    padding: '0 8px !important',
    boxShadow: 'none !important',
  },
})

const calendarCardStyle = css({
  background: '#fff',
  overflow: 'hidden',
})

const weekdayHeaderStyle = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  borderBottom: '1px solid #F2F2F2',
  background: '#F2F2F2',
})

const weekdayCellStyle = css({
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 'bold',
  color: '#676767',
})

const weekdaySunStyle = css({
  color: RATE_CALENDAR_WEEKDAY_TEXT_COLORS.sunday,
})

const weekdaySatStyle = css({
  color: RATE_CALENDAR_WEEKDAY_TEXT_COLORS.saturday,
})

const calendarGridStyle = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gridAutoRows: 'minmax(88px, auto)',
})

const dayCellStyle = css({
  borderRight: '1px solid #EFEFEF',
  borderBottom: '1px solid #EFEFEF',
  padding: '16px 0',
  background: '#fff',
  minHeight: 88,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  cursor: 'default',
  '&:nth-of-type(7n)': {
    borderRight: 'none',
  },
})

// 前後月でも編集可の日は白のまま。グレーは「編集不可」だけを示す
const disabledCellStyle = css({
  background: DISABLED_CELL_BACKGROUND,
  cursor: 'default',
})

const dayCellHeaderStyle = css({
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  minHeight: 16,
})

const dayNumberStyle = css({
  fontSize: 14,
  fontWeight: 'bold',
  color: '#272727',
  lineHeight: 1,
  textAlign: 'center',
})

// 当月の土日色はこの後に重ねるので、当月の過去日でも曜日色は残る
const dayNumberDisabledStyle = css({
  color: RATE_CALENDAR_WEEKDAY_TEXT_COLORS.muted,
})

const dayNumberSunStyle = css({
  color: RATE_CALENDAR_WEEKDAY_TEXT_COLORS.sunday,
})

const dayNumberSatStyle = css({
  color: RATE_CALENDAR_WEEKDAY_TEXT_COLORS.saturday,
})

const memoButtonStyle = css({
  position: 'absolute',
  top: 0,
  // Figma: right ~6.25% of cell (~144px)
  right: 9,
  border: 'none',
  background: 'transparent',
  padding: 0,
  cursor: 'pointer',
  lineHeight: 0,
  '&:disabled': {
    cursor: 'default',
    opacity: 0.4,
  },
})

const memoIconStyle = css({
  width: 16,
  height: 16,
  display: 'block',
})

const rankSelectWrapStyle = css({
  position: 'relative',
  // Figma: ピル左右 ~15%（セル幅144pxで約21px）
  margin: '8px 21px 0',
})

const rankPillStyle = css({
  position: 'relative',
  width: '100%',
  height: 28,
  border: 'none',
  borderRadius: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: '0 10px',
})

// opacity だと白文字まで薄くなるため、塗りだけをセル背景で半分覆う（ランク色は任意文字列もあり alpha 合成できない）
const disabledRankPillStyle = css({
  cursor: 'default',
  backgroundImage: `linear-gradient(${DISABLED_PILL_OVERLAY}, ${DISABLED_PILL_OVERLAY})`,
})

const rankPillTextStyle = css({
  fontSize: 12,
  fontWeight: 'bold',
  color: '#fff',
  lineHeight: 1,
})

const rankChevronStyle = css({
  position: 'absolute',
  right: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 0,
  height: 0,
  borderLeft: '4px solid transparent',
  borderRight: '4px solid transparent',
  borderTop: '4px solid #fff',
})

const rankPopoverStyle = (position: { top: number; left: number; width: number }) =>
  css({
    position: 'fixed',
    top: position.top,
    left: position.left,
    width: position.width,
    maxHeight: RANK_POPOVER_MAX_HEIGHT,
    overflowY: 'auto',
    padding: '4px 0',
    zIndex: 120,
  })

const rankPopupItemStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  fontSize: 12,
  color: '#272727',
  cursor: 'pointer',
  '&:hover': {
    background: '#F7F7F7',
  },
})

const rankPopupItemDisabledStyle = css({
  cursor: 'default',
  opacity: 0.55,
  color: '#676767',
  pointerEvents: 'none',
  '&:hover': {
    background: 'transparent',
  },
})

const rankPopupColorStyle = css({
  width: 10,
  height: 10,
  borderRadius: '50%',
  flexShrink: 0,
})
